(() => {
  if (window.nzSourceLoaded) return;
  window.nzSourceLoaded = true;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 1024px)');
  const panelState = new WeakMap();
  const megaTimers = new WeakMap();
  let savedOverflow;

  function openPanel(panel, trigger) {
    if (!panel || panel.open) return;
    if (!document.querySelector('dialog.nz-panel[open]')) {
      savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
    panelState.set(panel, { trigger, closing: false });
    panel.showModal();
    panel.getBoundingClientRect();
    panel.classList.add('nz-open');
    trigger?.setAttribute('aria-expanded', 'true');
  }

  function closePanel(panel) {
    const state = panelState.get(panel);
    if (!panel?.open || state?.closing) return;
    if (state) state.closing = true;
    panel.classList.remove('nz-open');
    const finish = () => {
      panel.close();
      state?.trigger?.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('dialog.nz-panel[open]')) {
        document.documentElement.style.overflow = savedOverflow ?? '';
      }
      state?.trigger?.focus();
      panelState.delete(panel);
    };
    if (reducedMotion.matches) finish();
    else window.setTimeout(finish, 500);
  }

  function setMega(details, open) {
    window.clearTimeout(megaTimers.get(details));
    if (open) {
      details.open = true;
      details.getBoundingClientRect();
      details.classList.add('nz-mega-open');
    } else {
      details.classList.remove('nz-mega-open');
      if (reducedMotion.matches) details.open = false;
      else megaTimers.set(details, window.setTimeout(() => { details.open = false; }, 200));
    }
    details.querySelector('summary')?.setAttribute('aria-expanded', String(open));
  }

  function initializeStickyHeaders(root = document) {
    root.querySelectorAll('[data-nz-sticky-header]').forEach((header) => {
      if (header.dataset.nzStickyReady) return;
      const type = header.dataset.nzStickyType || 'none';
      if (type === 'none') return;
      header.dataset.nzStickyReady = 'true';
      const slot = header.closest('.nz-header-slot');
      if (!slot) return;
      let lastScrollY = window.scrollY;
      let frame = 0;
      const update = () => {
        frame = 0;
        if (!header.isConnected) {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
          return;
        }
        const scrollY = window.scrollY;
        const threshold = slot.getBoundingClientRect().top + scrollY;
        const stuck = scrollY > 0 && scrollY >= threshold;
        header.toggleAttribute('data-nz-stuck', stuck);
        slot.toggleAttribute('data-nz-stuck', stuck);
        if (type === 'reduce-logo-size') {
          header.toggleAttribute('data-nz-sticky-compact', stuck);
          slot.toggleAttribute('data-nz-sticky-compact', stuck);
        }
        if (type === 'on-scroll-up') {
          const visible = !stuck || scrollY <= lastScrollY || header.contains(document.activeElement);
          header.dataset.nzStickyVisible = String(visible);
          slot.dataset.nzStickyVisible = String(visible);
        }
        lastScrollY = scrollY;
      };
      const onScroll = () => {
        if (!frame) frame = window.requestAnimationFrame(update);
      };
      header.addEventListener('focusin', () => {
        if (type === 'on-scroll-up') header.dataset.nzStickyVisible = 'true';
      });
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    });
  }

  function initializeSearch(root = document) {
    root.querySelectorAll('.nz-search-form').forEach((form) => {
      if (form.dataset.nzSearchReady) return;
      const input = form.querySelector('input[type="search"]');
      const reset = form.querySelector('.nz-search-reset');
      if (!input || !reset) return;
      form.dataset.nzSearchReady = 'true';
      const update = () => reset.classList.toggle('hidden', input.value.length === 0);
      input.addEventListener('input', update);
      reset.addEventListener('click', () => {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
        const predictive = form.closest('predictive-search');
        if (predictive) predictive.removeAttribute('loading');
      });
      update();
    });
  }

  async function loadMore(link) {
    if (link.dataset.nzLoading) return;
    const grid = document.getElementById(link.dataset.nzGrid);
    if (!grid) return;
    link.dataset.nzLoading = 'true';
    link.setAttribute('aria-disabled', 'true');
    grid.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) throw new Error('Invalid collection URL');
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Collection page unavailable');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = page.getElementById(grid.id);
      if (!nextGrid) throw new Error('Collection grid unavailable');
      const cards = [...nextGrid.querySelectorAll(':scope > .nz-card')];
      if (!cards.length) throw new Error('No more collection products');
      grid.append(...cards);
      initialize(grid);
      const pagination = link.closest('[data-nz-pagination]');
      const next = page.querySelector(`[data-nz-load-more][data-nz-grid="${grid.id}"]`);
      if (next) link.href = new URL(next.getAttribute('href'), url).href;
      else link.hidden = true;
      const status = pagination?.querySelector('[data-nz-load-status]');
      if (status) status.textContent = `${cards.length} ${link.dataset.nzLoadedLabel}`;
      cards[0].querySelector('a')?.focus({ preventScroll: true });
    } catch {
      window.location.assign(link.href);
    } finally {
      delete link.dataset.nzLoading;
      link.removeAttribute('aria-disabled');
      grid.removeAttribute('aria-busy');
    }
  }

  function initializeReveals(root = document) {
    const reveals = root.querySelectorAll('.nz-reveal:not([data-nz-reveal-ready])');
    if (!reveals.length) return;
    reveals.forEach((element) => element.dataset.nzRevealReady = 'true');
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      reveals.forEach((element) => element.dataset.nzRevealVisible = 'true');
      return;
    }
    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.dataset.nzRevealVisible = 'true';
        instance.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    reveals.forEach((element) => observer.observe(element));
  }

  function initializeRecommendationReveals(root = document) {
    const recommendations = [];
    if (root.matches?.('product-recommendations.nz-recommendations')) recommendations.push(root);
    recommendations.push(...root.querySelectorAll('product-recommendations.nz-recommendations'));
    recommendations.forEach((container) => {
      if (container.dataset.nzRevealObserver) return;
      container.dataset.nzRevealObserver = 'true';
      const observer = new MutationObserver(() => initializeReveals(container));
      observer.observe(container, { childList: true, subtree: true });
      initializeReveals(container);
    });
  }

  function initializeFloatingAtc(root = document) {
    root.querySelectorAll('[data-nz-floating-atc]').forEach((bar) => {
      if (bar.dataset.nzReady) return;
      const form = document.getElementById(bar.dataset.nzFormId);
      const submit = form?.querySelector('button[name="add"]');
      const main = bar.closest('.nz-product-main') || document.querySelector('.nz-product-main');
      const trigger = bar.querySelector('[data-nz-floating-submit]');
      const label = bar.querySelector('[data-nz-floating-label]');
      const price = bar.querySelector('[data-nz-floating-price]');
      const sourcePrice = document.getElementById(`price-${bar.dataset.nzFormId.replace(/^product-form-/, '')}`);
      if (!form || !submit || !trigger || !main) return;
      bar.dataset.nzReady = 'true';
      const update = () => {
        bar.hidden = desktop.matches ? main.getBoundingClientRect().bottom > 0 : submit.getBoundingClientRect().bottom > 0;
        trigger.disabled = submit.disabled || submit.getAttribute('aria-disabled') === 'true';
        const sourceLabel = submit.querySelector('span')?.textContent.trim();
        if (label && sourceLabel && label.textContent !== sourceLabel) label.textContent = sourceLabel;
        const sourceValue = sourcePrice?.querySelector('.price-item--last,.price-item--regular')?.textContent.trim();
        const buyPrice = submit.querySelector('[data-nz-buy-price]');
        if (price && sourceValue && price.textContent !== sourceValue) price.textContent = sourceValue;
        if (buyPrice && sourceValue && buyPrice.textContent !== sourceValue) buyPrice.textContent = sourceValue;
      };
      trigger.addEventListener('click', () => { if (!trigger.disabled) submit.click(); });
      const observer = new MutationObserver(update);
      observer.observe(submit, { attributes: true, childList: true, subtree: true });
      if (sourcePrice) observer.observe(sourcePrice, { childList: true, subtree: true });
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  function initialize(root = document) {
    const main = document.querySelector('main');
    if (main?.querySelector('.nz-surface')) main.classList.add('nz-page');
    initializeStickyHeaders(root);
    initializeSearch(root);
    initializeFloatingAtc(root);
    initializeReveals(root);
    initializeRecommendationReveals(root);
    document.querySelectorAll('product-modal').forEach((modal) => {
      const sectionId = modal.dataset.section || modal.id.replace('ProductModal-', '');
      if (document.getElementById(`MainProduct-${sectionId}`)?.classList.contains('nz-product')) modal.classList.add('nz-product-modal');
    });
    root.querySelectorAll('dialog.nz-panel').forEach((panel) => {
      if (panel.dataset.nzReady) return;
      panel.dataset.nzReady = 'true';
      panel.addEventListener('cancel', (event) => {
        event.preventDefault();
        closePanel(panel);
      });
      panel.addEventListener('click', (event) => {
        if (event.target !== panel) return;
        const box = panel.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closePanel(panel);
      });
    });
    root.querySelectorAll('[data-nz-footer]').forEach((details) => { details.open = desktop.matches; });
    root.querySelectorAll('.nz-product-description .nz-product-intro').forEach((intro) => {
      if (intro.dataset.nzReady) return;
      intro.dataset.nzReady = 'true';
      let accordion = null;
      [...intro.children].forEach((node) => {
        if (node.matches('h2,h3,h4')) {
          accordion = document.createElement('details');
          accordion.className = 'nz-product-specs';
          const summary = document.createElement('summary');
          summary.textContent = node.textContent;
          accordion.append(summary);
          node.replaceWith(accordion);
        } else if (accordion) {
          accordion.append(node);
        }
      });
    });
    root.querySelectorAll('[data-nz-gallery]').forEach((gallery) => {
      if (gallery.dataset.nzReady) return;
      gallery.dataset.nzReady = 'true';
      const track = gallery.querySelector('.nz-gallery-wrapper');
      const counter = gallery.querySelector('[data-nz-counter]');
      if (!track) return;
      const update = () => {
        const index = desktop.matches
          ? [...track.children].findIndex((slide) => slide.offsetTop >= gallery.scrollTop - 2)
          : Math.round(track.scrollLeft / (track.clientWidth || 1));
        if (counter) counter.textContent = `${Math.max(0, index) + 1} / ${track.children.length}`;
        gallery.querySelectorAll('[data-nz-thumbnail]').forEach((button) => {
          button.setAttribute('aria-pressed', String(Number(button.dataset.nzThumbnail) === Math.max(0, index)));
        });
      };
      gallery.querySelectorAll('[data-nz-thumbnail]').forEach((button) => {
        button.addEventListener('click', () => {
          const slide = track.children[Number(button.dataset.nzThumbnail)];
          if (!slide) return;
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          if (desktop.matches) gallery.scrollTo({ top: slide.offsetTop, behavior });
          else track.scrollTo({ left: slide.offsetLeft, behavior });
        });
      });
      track.addEventListener('scroll', update, { passive: true });
      gallery.addEventListener('scroll', update, { passive: true });
      track.querySelectorAll('img[data-src],img[src]').forEach((image) => {
        const useFallback = () => {
          const fallback = image.closest('.nz-gallery-item')?.dataset.mediaFallback;
          if (!fallback || image.dataset.nzFallbackUsed) return;
          image.dataset.nzFallbackUsed = 'true';
          image.removeAttribute('srcset');
          image.src = fallback;
        };
        image.addEventListener('error', useFallback);
        if (image.complete && image.naturalWidth === 0) useFallback();
      });
      update();
    });
    root.querySelectorAll('video[data-nz-autoplay]').forEach((video) => {
      if (video.dataset.nzReady) return;
      video.dataset.nzReady = 'true';
      video.muted = true;
      const toggle = video.closest('.nz-video')?.querySelector('[data-nz-video-toggle]');
      if (toggle) {
        toggle.hidden = false;
        video.controls = false;
        const update = () => {
          const playing = !video.paused;
          const label = playing ? toggle.dataset.nzPauseLabel : toggle.dataset.nzPlayLabel;
          toggle.setAttribute('aria-label', label);
          toggle.setAttribute('aria-pressed', String(playing));
          toggle.querySelector('.visually-hidden').textContent = label;
          toggle.querySelector('[data-nz-video-play]').hidden = playing;
          toggle.querySelector('[data-nz-video-pause]').hidden = !playing;
        };
        video.addEventListener('play', update);
        video.addEventListener('pause', update);
        toggle.addEventListener('click', () => {
          video.dataset.nzManualPause = String(!video.paused);
          if (video.paused) video.play().catch(() => {});
          else video.pause();
        });
        update();
      }
      if (!reducedMotion.matches) video.play().catch(() => {});
      else video.pause();
    });
    root.querySelectorAll('[data-nz-mega]').forEach((details) => {
      if (details.dataset.nzReady) return;
      details.dataset.nzReady = 'true';
      details.querySelector('summary').setAttribute('aria-expanded', String(details.open));
      details.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && desktop.matches) setMega(details, true);
      });
      details.addEventListener('pointerleave', () => {
        if (!details.contains(document.activeElement)) setMega(details, false);
      });
      details.addEventListener('focusout', (event) => {
        if (!details.contains(event.relatedTarget)) setMega(details, false);
      });
    });
    root.querySelectorAll('.nz-journal[data-nz-slider]').forEach((slider) => {
      if (slider.dataset.nzReady) return;
      slider.dataset.nzReady = 'true';
      const track = slider.querySelector('.nz-rail');
      const thumb = slider.querySelector('.nz-rail-scrollbar span');
      if (!track || !thumb) return;
      const update = () => {
        const ratio = Math.min(1, track.clientWidth / track.scrollWidth);
        const progress = track.scrollLeft / Math.max(1, track.scrollWidth - track.clientWidth);
        thumb.style.width = `${ratio * 100}%`;
        thumb.style.left = `${progress * (1 - ratio) * 100}%`;
      };
      track.addEventListener('scroll', update, { passive: true });
      const observer = new ResizeObserver(update);
      observer.observe(track);
      update();
    });
    root.querySelectorAll('[data-nz-faq-content]').forEach((content) => {
      if (content.dataset.nzReady) return;
      content.dataset.nzReady = 'true';
      // Enhance the store's own FAQ headings. No answers are synthesized.
      const children = Array.from(content.children);
      let group;
      let answer;
      children.forEach((child) => {
        if (child.tagName === 'H2') {
          group = document.createElement('div');
          group.className = 'nz-faq-group';
          content.insertBefore(group, child);
          group.append(child);
          answer = null;
        } else if (child.tagName === 'H3') {
          if (!group) {
            group = document.createElement('div');
            group.className = 'nz-faq-group';
            content.insertBefore(group, child);
          }
          const details = document.createElement('details');
          const summary = document.createElement('summary');
          summary.textContent = child.textContent;
          const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          arrow.setAttribute('aria-hidden', 'true');
          arrow.setAttribute('viewBox', '0 0 24 24');
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', 'M4 12h16m-6-6 6 6-6 6');
          arrow.append(path);
          summary.append(arrow);
          answer = document.createElement('div');
          answer.className = 'nz-faq-answer';
          details.append(summary, answer);
          group.append(details);
          child.remove();
        } else if (answer) answer.append(child);
        else if (group) group.append(child);
      });
    });
  }

  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-nz-load-more]');
    if (more && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      loadMore(more);
      return;
    }
    const megaSummary = event.target.closest('[data-nz-mega] > summary');
    if (megaSummary) {
      event.preventDefault();
      const details = megaSummary.parentElement;
      setMega(details, !details.classList.contains('nz-mega-open'));
    }
    const zoom = event.target.closest('.nz-gallery-zoom');
    if (zoom) {
      const modal = document.querySelector(zoom.closest('modal-opener').dataset.modal);
      const dialog = modal?.querySelector('[role="dialog"]');
      if (modal?.hasAttribute('open') && dialog && typeof trapFocus === 'function') trapFocus(dialog, dialog.querySelector('button') || dialog);
    }
    const opener = event.target.closest('[data-nz-open]');
    if (opener) { event.preventDefault(); openPanel(document.getElementById(opener.dataset.nzOpen), opener); }
    const closer = event.target.closest('[data-nz-close]');
    if (closer) closePanel(closer.closest('dialog'));
    const dismiss = event.target.closest('[data-nz-dismiss]');
    if (dismiss) {
      dismiss.closest('[data-nz-announcement]').hidden = true;
      window.dispatchEvent(new Event('resize'));
    }
    const descriptionToggle = event.target.closest('[data-nz-description-toggle]');
    if (descriptionToggle) {
      const description = document.getElementById(descriptionToggle.getAttribute('aria-controls'));
      const expanded = description.classList.toggle('nz-expanded');
      descriptionToggle.setAttribute('aria-expanded', String(expanded));
      descriptionToggle.textContent = expanded ? descriptionToggle.dataset.less : descriptionToggle.dataset.more;
    }
    const railButton = event.target.closest('[data-nz-slide]');
    if (railButton) {
      const container = railButton.closest('[data-nz-slider]');
      const gallery = container?.matches('[data-nz-gallery]') ? container : null;
      const track = gallery?.querySelector('.nz-gallery-wrapper') || container?.querySelector('.nz-rail');
      if (!track) return;
      const step = track.firstElementChild?.getBoundingClientRect().width || track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      if (gallery && desktop.matches) {
        gallery.scrollBy({ top: Number(railButton.dataset.nzSlide) * (track.firstElementChild?.getBoundingClientRect().height || gallery.clientHeight), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      } else {
        track.scrollBy({ left: Number(railButton.dataset.nzSlide) * (step + gap), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      }
    }
    if (!event.target.closest('[data-nz-mega]')) document.querySelectorAll('[data-nz-mega][open]').forEach((details) => setMega(details, false));
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      const modal = event.target.closest('product-modal.nz-product-modal[open]');
      const dialog = modal?.querySelector('[role="dialog"]');
      if (dialog && typeof getFocusableElements === 'function') {
        const elements = getFocusableElements(dialog).filter((element) => element.tabIndex >= 0 && element.getClientRects().length);
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (first && ((event.shiftKey && event.target === first) || (!event.shiftKey && event.target === last))) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }
    }
    if (event.key !== 'Escape') return;
    const productModal = document.querySelector('product-modal.nz-product-modal[open]');
    if (productModal) {
      event.preventDefault();
      productModal.hide();
      return;
    }
    document.querySelectorAll('[data-nz-mega][open]').forEach((details) => setMega(details, false));
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-nz-country]')) event.target.form.requestSubmit();
  });
  desktop.addEventListener('change', () => {
    document.querySelectorAll('[data-nz-footer]').forEach((details) => { details.open = desktop.matches; });
    if (desktop.matches) document.querySelectorAll('.nz-panel--left[open]').forEach(closePanel);
  });
  reducedMotion.addEventListener('change', () => {
    document.querySelectorAll('video[data-nz-autoplay]').forEach((video) => {
      if (reducedMotion.matches) video.pause();
      else if (video.dataset.nzManualPause !== 'true') video.play().catch(() => {});
    });
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('product-info:loaded', (event) => initialize(event.target));
  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const gallery = document.querySelector(`#MainProduct-${data.sectionId} [data-nz-gallery]`);
      const mediaId = data.variant?.featured_media?.id;
      const media = mediaId && gallery?.querySelector(`[data-media-id="${data.sectionId}-${mediaId}"]`);
      if (media) {
        const track = gallery.querySelector('.nz-gallery-wrapper');
        const scroller = desktop.matches ? gallery : track;
        scroller?.scrollTo({ left: desktop.matches ? 0 : media.offsetLeft, top: desktop.matches ? media.offsetTop : 0, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
})();
